import type { DataProxy, Collection, QueryParams, Model, PropertiesObject } from '@kp/js-model';
import { BaseModel } from './BaseModel';
import type { CancelTokenSource } from 'axios';
import { instance as api, getTokenSource } from '@/helpers/api';

export interface ProxyEvent {
    msg(): string;
}

export class ServerTotalUpdated implements ProxyEvent {
    constructor(public readonly serverTotal: number) { }
    public msg(): string {
        return `Server total updated: ${this.serverTotal}`;
    }
}

export type ProxyEventObserver = (e: ProxyEvent) => void;

const globalCancelTokens: { [key: string]: CancelTokenSource } = {};
export class BaseDataProxy implements DataProxy {
    private _serverTotal: number = 0;
    private observers: ProxyEventObserver[] = [];
    public globalCancelTokenId: string | null = null;

    protected getIdName<M extends Model>(m: M): string {
        if (m instanceof BaseModel) {
            return m.getIdName();
        } else {
            return 'id';
        }
    }

    /**
     * Fetches a single record from the backend. The backend needs to offer a
     * /[entity]/get endpoint, which returns a single record by id (?id=n).
     *
     * @param m
     * @param queryParams
     * @returns
     */
    async fetch<M extends Model>(m: M, queryParams?: QueryParams): Promise<PropertiesObject | null> {
        const qp: any = {};
        qp[this.getIdName(m)] = m.get(this.getIdName(m));
        const response = await api().get(`/${m.getClassName()}/get`, { params: { ...qp, ...queryParams } });
        this.serverTotal = 0;

        return response.data || null;
    }

    async create<M extends Model>(m: M, queryParams?: QueryParams): Promise<PropertiesObject | null> {
        return await this.update(m, queryParams);
    }

    async update<M extends Model>(m: M, queryParams?: QueryParams): Promise<PropertiesObject | null> {
        const updateData = {
            [this.getIdName(m)]: m.get(this.getIdName(m)),
            ...m.getDirtyProps()
        };
        delete updateData.proxy;
        const response = await api().post(`/${m.getClassName()}/store`, updateData, {
            params: queryParams,
            headers: {
                'Content-Type': 'application/json'
            }
        });
        return response?.data || {};
    }

    async delete<M extends Model>(m: M, queryParams?: QueryParams): Promise<PropertiesObject | null> {
        const response = await api().get(`/${m.getClassName()}/destroy?id=${m.get(this.getIdName(m))}`);
        return response?.data || {};
    }

    async query<M extends Model, C extends Collection<M>>(
        collection: C,
        queryParams?: QueryParams
    ): Promise<PropertiesObject[]> {
        const response = await api().post(`/${collection.getModelClassName()}/list`, queryParams, {
            cancelToken: this.createCancelToken(),
        });
        this.serverTotal = response?.data?.total || 0;

        return response?.data?.records || [];
    }

    public get serverTotal(): number {
        return this._serverTotal;
    }

    public set serverTotal(total: number) {
        this._serverTotal = total;
        const e = new ServerTotalUpdated(total);
        for (const o of this.observers) {
            o(e);
        }
    }

    public addObserver(o: ProxyEventObserver) {
        if (!this.observers.includes(o)) {
            this.observers.push(o);
        }
    }
    public removeObserver(o: ProxyEventObserver) {
        const index = this.observers.indexOf(o);
        if (index > -1) {
            this.observers.splice(index, 1);
        }
    }

    protected _cancelTokenSrc: CancelTokenSource | null = null;

    /**
    * Creates a new cancel token, cancels an ALREADY issued token for this collection.
    * If the 'globalCancelTokenId' attribute is set on this collection, a global token
    * will be used, otherwise an instance-only token is used.
    */
    public createCancelToken() {
        let tokenSrc = null;
        const cancelTokenIdent = this.globalCancelTokenId;

        // Cancel an already running request, if applicable:
        if (cancelTokenIdent && globalCancelTokens[cancelTokenIdent]) {
            tokenSrc = globalCancelTokens[cancelTokenIdent];
        } else {
            tokenSrc = this._cancelTokenSrc;
        }

        if (tokenSrc) {
            tokenSrc.cancel();
            this.removeCancelToken();
        }

        tokenSrc = getTokenSource();

        // If requested, store a global cancel token for later cancellation:
        if (cancelTokenIdent) {
            globalCancelTokens[cancelTokenIdent] = tokenSrc;
        }
        this._cancelTokenSrc = tokenSrc;
        return tokenSrc.token;
    }

    public removeCancelToken() {
        const cancelTokenIdent = this.globalCancelTokenId;
        if (cancelTokenIdent) {
            delete globalCancelTokens[cancelTokenIdent];
        }
        this._cancelTokenSrc = null;
    }
}
